import React from 'react';
import {useTranslation} from 'react-i18next';
import styled from 'styled-components';
import '@techsmith/techsmith-smartplayer/dist/techsmith-smart-player.css';
import '../../static/css/modal.less';
import {TscStyles} from '@techsmith/tsc-cloud-style-guide';
import Constants, {EntryPoint} from '../../constants/Constants';
import ProgressLoader from '../util/ProgressLoader';
import {IWorkerState} from '../../model/workerState';
import {StyledAnchor} from '../util/StyledElements';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {mediaStore} from '../../context/mediaProvider';
import config from '../../service/config';

const PizzaTrackerContainerDiv = styled.div<{isEmbedView: boolean}>`
   margin: auto;
   box-sizing: border-box;
   text-align: center;
   justify-content: center;
   align-self: center;
   display: flex;
   flex-direction: column;
   background-color: black;
   padding: 3.375rem 2rem;
   ${props => props.isEmbedView ? `
      height: 100%;
      width: 100%;
   ` : ''}

   a {
      color: ${TscStyles.color.action.primary};
   }

   h2 {
       color: white;
       margin-bottom: 0.25rem;
   }

   h3 {
       color: ${TscStyles.color.text.lightMedium};
       font-weight: 500;
       margin-top: 0;
   }
`;

export const PizzaTrackerBase: React.FC<IPizzaTrackerProps & IPizzaTrackerStateMappedProps> = ({isOwnerOrAdmin, HasEncodingError, EncodingProgress, MediaStoreWorkerState}) => {
   const {t} = useTranslation();

   return (
      <PizzaTrackerContainerDiv
         id="tscMediaContent"
         data-test-id="pizza-tracker"
         isEmbedView={config.entryPoint === EntryPoint.embed}
      >
         {MediaStoreWorkerState.isWorking ? (
            <>
               <h2>
                  {t('FetchingMediaProgressTitle')}
               </h2>
               <h3>
                  {t('FetchingMediaProgressSubTitle')}
               </h3>
            </>
         ) : MediaStoreWorkerState.hasError || HasEncodingError ? (
            <>
               <h2>
                  {t('MediaProcessingFailedTitle')}
               </h2>
               {isOwnerOrAdmin && (
                  <h3>
                     {t('MediaProcessingFailedSubtitle')}
                     {' '}
                     <StyledAnchor href={Constants.navigation.contactSupportLink}>
                        {t('ContactSupport')}
                     </StyledAnchor>
                  </h3>
               )}
            </>
         ) : !MediaStoreWorkerState.isWorking && !MediaStoreWorkerState.hasError && (
            <>
               <ProgressLoader uploadProgress={EncodingProgress} />
               <h2 className="t-media-proccessing">
                  {t('MediaBeingProcessedTitle')}
               </h2>
               <h3>
                  {t(isOwnerOrAdmin ? 'MediaBeingProcessedSubtitleOwner' : 'MediaBeingProcessedSubtitleNonOwner')}
               </h3>
            </>
         )}
      </PizzaTrackerContainerDiv>
   );
};

export interface IPizzaTrackerProps {
   isOwnerOrAdmin: boolean;
}

export interface IPizzaTrackerStateMappedProps {
   HasEncodingError: boolean;
   EncodingProgress: number;
   MediaStoreWorkerState: IWorkerState;
}

const mapStatesToProps = (
   {isOwnerOrAdmin}: IPizzaTrackerProps,
   {HasEncodingError, EncodingProgress, MediaStoreWorkerState}: Partial<IPizzaTrackerStateMappedProps>
): IPizzaTrackerProps & IPizzaTrackerStateMappedProps => ({isOwnerOrAdmin, HasEncodingError, EncodingProgress, MediaStoreWorkerState});

export default withMemoizedContexts(mapStatesToProps, mediaStore)(PizzaTrackerBase);
